import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTurnstileSitekey } from '../config/turnstile';

// Render explícito: o api.js NÃO auto-renderiza; chamamos window.turnstile.render
// no ref após o script carregar. Permite controlar callbacks + cleanup.
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Watchdog: se após o render nenhum token/erro/timeout vier neste prazo, tratamos
// como falha. Cobre o desafio que TRAVA sem disparar callback (rede alcança o
// api.js mas bloqueia o host do desafio — reproduzido no QA 2026-07-21). Generoso
// p/ não punir rede lenta; se o token vier depois, o callback limpa o estado.
const WATCHDOG_MS = 30_000;

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
  'timeout-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

// Module-level singleton — garante que o api.js entra no DOM uma única vez
// mesmo que o widget monte/desmonte várias vezes (navegação entre steps).
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window !== 'undefined' && window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src^="https://challenges.cloudflare.com/turnstile"]',
    );
    if (existing) {
      if (window.turnstile) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener('load', () => resolve());
    s.addEventListener('error', () => reject(new Error('turnstile script failed')));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

interface TurnstileWidgetProps {
  /** Chamado com o token quando o desafio é resolvido. */
  onToken: (token: string) => void;
  /** Chamado quando o token expira ou o widget dá erro — limpar o token no pai. */
  onExpire?: () => void;
}

/**
 * Cloudflare Turnstile widget (D-29). Sem dependência npm — carrega o script
 * oficial e usa render explícito. A sitekey vem de config/turnstile.ts
 * (VITE_TURNSTILE_SITEKEY em prod, test sitekey em dev).
 */
export function TurnstileWidget({ onToken, onExpire }: TurnstileWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [failed, setFailed] = useState(false);
  // Distinto de `failed` (erro de script/config — terminal): o DESAFIO em si
  // falhou/expirou/deu timeout. Recuperável via reset. Sem isto a falha era
  // SILENCIOSA (widget vazio + botão desabilitado, sem mensagem) — QA 2026-07-21.
  const [challengeError, setChallengeError] = useState(false);

  // Mantém os callbacks mais recentes sem re-disparar o effect de render.
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;

  useEffect(() => {
    let cancelled = false;

    let sitekey: string;
    try {
      sitekey = getTurnstileSitekey();
    } catch (e) {
      // Prod sem VITE_TURNSTILE_SITEKEY (erro de config). Não renderiza widget;
      // o pai mantém o submit bloqueado por falta de token.
      console.error(e);
      setFailed(true);
      return;
    }

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey,
          theme: 'light',
          callback: (token: string) => {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            setChallengeError(false);
            onTokenRef.current(token);
          },
          'expired-callback': () => onExpireRef.current?.(),
          // O desafio falhou/expirou: limpa o token E torna a falha VISÍVEL
          // (antes só limpava o token → falha silenciosa; achado QA 2026-07-21).
          'error-callback': () => {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            onExpireRef.current?.();
            setChallengeError(true);
          },
          'timeout-callback': () => {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            onExpireRef.current?.();
            setChallengeError(true);
          },
        });
        // Arma o watchdog: cobre o desafio que TRAVA sem callback nenhum.
        watchdogRef.current = setTimeout(() => setChallengeError(true), WATCHDOG_MS);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setFailed(true);
      });

    return () => {
      cancelled = true;
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget já removido — noop */
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  // Re-executa o desafio no MESMO container (que nunca desmonta → widgetId segue
  // válido) e some com a mensagem. Só o overlay de erro aparece/some.
  const handleRetry = (): void => {
    setChallengeError(false);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        if (watchdogRef.current) clearTimeout(watchdogRef.current);
        watchdogRef.current = setTimeout(() => setChallengeError(true), WATCHDOG_MS);
      } catch {
        /* widget indisponível; recarregar a página resolve */
      }
    }
  };

  if (failed) {
    return (
      <div className="turnstile-error" role="alert">
        {t('turnstile.load_error')}
      </div>
    );
  }

  return (
    <div className="turnstile-widget">
      <p className="turnstile-label">{t('turnstile.label')}</p>
      <div ref={containerRef} data-testid="turnstile-container" />
      {challengeError && (
        <div className="turnstile-error" role="alert" data-testid="turnstile-challenge-error">
          <p>{t('turnstile.challenge_error')}</p>
          <button type="button" className="turnstile-retry" onClick={handleRetry}>
            {t('turnstile.retry')}
          </button>
        </div>
      )}
    </div>
  );
}
